// src/pages/AbTests.jsx
// /ab-tests — standalone A/B test list + create.
//
// Tests used to be reachable only by drilling into a Project. Projects are
// gone from the navigation, so this is their own home. A test compares either
// two external URLs (the long-standing traffic_split behaviour) or two pages
// built in the app's editor.
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authFetch } from '../lib/authFetch';
import {
  Card, CardContent, Button, Input, Modal, ModalFooter, Badge, Select,
} from '../components/ui';
import { formatNumber, formatDate, cn } from '../lib/utils';
import {
  Plus, FlaskConical, Loader2, BarChart3, Trash2, Trophy,
} from 'lucide-react';
import toast from 'react-hot-toast';

const BLANK_VARIANT = { name: '', destination_url: '', funnel_id: '', funnel_step_id: '', weight: 50 };

// Uplift of a variant against the control. Returned as null when there isn't
// enough data to say anything — showing "0%" for an untested variant reads as
// a real result, which it isn't.
function uplift(variant, control) {
  if (!control || variant.id === control.id) return null;
  if (!variant.visits || !control.visits || !control.conversions) return null;
  const a = variant.conversions / variant.visits;
  const b = control.conversions / control.visits;
  if (!b) return null;
  return Math.round(((a - b) / b) * 1000) / 10;
}

function cvr(v) {
  return v.visits ? `${Math.round((v.conversions / v.visits) * 1000) / 10}%` : '—';
}

export default function AbTests() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    name: '',
    variant_source: 'page',
    variants: [{ ...BLANK_VARIANT, name: 'A (control)' }, { ...BLANK_VARIANT, name: 'B' }],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Pages and funnels are both valid variant targets — either can be the
      // thing whose conversion rate you want to compare.
      const [testsRes, pagesRes] = await Promise.all([
        authFetch('/api/ab?resource=tests').then(r => r.json()),
        authFetch('/api/funnels?resource=funnels').then(r => r.json()),
      ]);
      if (testsRes.success) setTests(testsRes.tests || []);
      else toast.error(testsRes.error || 'Failed to load tests');
      if (pagesRes.success) setPages(pagesRes.funnels || []);
    } catch (e) {
      console.error('A/B load error:', e);
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setVariant = (i, patch) => setForm(f => ({
    ...f,
    variants: f.variants.map((v, idx) => (idx === i ? { ...v, ...patch } : v)),
  }));

  const handleCreate = async () => {
    const name = form.name.trim();
    if (!name) return toast.error('Give your test a name');

    const filled = form.variants.filter(v =>
      form.variant_source === 'page' ? v.funnel_id : (v.destination_url || '').trim());
    if (filled.length < 2) {
      return toast.error(form.variant_source === 'page'
        ? 'Pick a page for both variants'
        : 'Enter a URL for both variants');
    }

    setCreating(true);
    try {
      const res = await authFetch('/api/ab?resource=tests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          name,
          // traffic_split is the mode that mints a shareable slug and splits
          // visitors between variants — the right mode for both target types.
          test_type: 'traffic_split',
          variant_source: form.variant_source,
          variants: form.variants.map((v, i) => ({
            name: v.name.trim() || `Variant ${String.fromCharCode(65 + i)}`,
            destination_url: form.variant_source === 'url' ? v.destination_url.trim() : null,
            funnel_id: form.variant_source === 'page' ? v.funnel_id || null : null,
            funnel_step_id: form.variant_source === 'page' ? v.funnel_step_id || null : null,
            weight: Number(v.weight) || 50,
            is_control: i === 0,
          })),
        }),
      });
      const data = await res.json();
      if (!data.success && !data.test) {
        toast.error(data.error || 'Failed to create test');
        return;
      }
      toast.success('Test created');
      setShowCreate(false);
      const id = data.test?.id;
      if (id) navigate(`/ab-tests/${id}`);
      else load();
    } catch (e) {
      console.error('Create test error:', e);
      toast.error('Failed to create test');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (test) => {
    if (!window.confirm(`Delete "${test.name}"? Its variants and recorded results will be permanently removed.`)) return;
    try {
      const res = await authFetch('/api/ab?resource=tests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', testId: test.id }),
      });
      const data = await res.json();
      if (data.success === false) return toast.error(data.error || 'Failed to delete');
      toast.success('Test deleted');
      setTests(prev => prev.filter(t => t.id !== test.id));
    } catch {
      toast.error('Failed to delete');
    }
  };

  const openCreate = () => {
    setForm({
      name: '',
      variant_source: pages.length >= 2 ? 'page' : 'url',
      variants: [{ ...BLANK_VARIANT, name: 'A (control)' }, { ...BLANK_VARIANT, name: 'B' }],
    });
    setShowCreate(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">A/B Tests</h1>
          <p className="text-sm text-muted-foreground">
            Split traffic between two pages or URLs and see which converts better.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" /> New Test
        </Button>
      </div>

      {tests.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FlaskConical className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <h3 className="font-semibold mb-1">No tests yet</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm">
              Compare two versions of a page and let the traffic decide. You can test pages
              built here, or any two external URLs.
            </p>
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" /> Create your first test
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {tests.map(test => {
            const variants = [...(test.ab_test_variants || [])]
              .sort((a, b) => (a.is_control === b.is_control ? 0 : a.is_control ? -1 : 1));
            const control = variants.find(v => v.is_control) || variants[0];
            const best = variants.reduce((acc, v) => {
              const r = v.visits ? v.conversions / v.visits : 0;
              return !acc || r > acc.rate ? { id: v.id, rate: r } : acc;
            }, null);

            return (
              <Card key={test.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Link to={`/ab-tests/${test.id}`} className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold hover:text-primary transition-colors">{test.name}</span>
                        <Badge className={cn(
                          'text-[10px] px-1.5 py-0.5',
                          test.status === 'running' ? 'bg-green-500' : 'bg-muted-foreground/60'
                        )}>
                          {test.status || 'draft'}
                        </Badge>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">
                          {test.variant_source === 'page' ? 'Pages' : 'URLs'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatNumber(test.total_visits || 0)} visits · {formatNumber(test.total_conversions || 0)} conversions
                        {test.created_at && ` · created ${formatDate(test.created_at)}`}
                      </p>
                    </Link>
                    <div className="flex items-center gap-1">
                      <Link to={`/ab-tests/${test.id}/analytics`}
                        className="p-2 hover:bg-muted rounded-lg text-muted-foreground" title="Analytics">
                        <BarChart3 className="h-4 w-4" />
                      </Link>
                      <button onClick={() => handleDelete(test)}
                        className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-destructive"
                        title="Delete test">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {variants.length > 0 && (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {variants.map(v => {
                        const lift = uplift(v, control);
                        return (
                          <div key={v.id} className="rounded-lg border border-border p-2.5">
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="text-sm font-medium truncate">{v.name}</span>
                              {v.is_control && (
                                <Badge variant="secondary" className="text-[10px] px-1 py-0">control</Badge>
                              )}
                              {best && best.id === v.id && best.rate > 0 && (
                                <Trophy className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                              )}
                            </div>
                            <div className="flex items-baseline gap-3 text-xs text-muted-foreground">
                              <span>{formatNumber(v.visits || 0)} visits</span>
                              <span>{formatNumber(v.conversions || 0)} conv.</span>
                              <span className="font-semibold text-foreground">{cvr(v)}</span>
                              {lift !== null && (
                                <span className={cn('font-medium', lift >= 0 ? 'text-emerald-600' : 'text-red-500')}>
                                  {lift >= 0 ? '+' : ''}{lift}%
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="New A/B Test" size="lg">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Test name</label>
            <Input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Headline test — long vs short"
              autoFocus
            />
          </div>

          <div className="flex gap-2">
            {[
              { id: 'page', label: 'Pages built here' },
              { id: 'url', label: 'External URLs' },
            ].map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => setForm(f => ({ ...f, variant_source: o.id }))}
                className={cn(
                  'flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                  form.variant_source === o.id
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted'
                )}
              >
                {o.label}
              </button>
            ))}
          </div>

          {form.variant_source === 'page' && pages.length < 2 && (
            <div className="rounded-lg border border-amber-300/50 bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5 text-sm">
              You need at least two pages to test one against another.{' '}
              <Link to="/landing-pages" className="font-medium text-primary hover:underline">Create a landing page</Link>{' '}
              or switch to external URLs.
            </div>
          )}

          {form.variants.map((v, i) => (
            <div key={i} className="rounded-lg border border-border p-3 space-y-2.5">
              <div className="flex items-center gap-2">
                <Input
                  value={v.name}
                  onChange={e => setVariant(i, { name: e.target.value })}
                  placeholder={`Variant ${String.fromCharCode(65 + i)}`}
                  className="flex-1"
                />
                <div className="w-24">
                  <Input
                    type="number"
                    min="1"
                    max="99"
                    value={v.weight}
                    onChange={e => setVariant(i, { weight: e.target.value })}
                    hint="% split"
                  />
                </div>
              </div>

              {form.variant_source === 'page' ? (
                <Select
                  value={v.funnel_id}
                  onChange={e => setVariant(i, { funnel_id: e.target.value })}
                >
                  <option value="">Select a page…</option>
                  {pages.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} {p.kind === 'page' ? '(page)' : '(funnel)'}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  value={v.destination_url}
                  onChange={e => setVariant(i, { destination_url: e.target.value })}
                  placeholder="https://example.com/variant-a"
                />
              )}
            </div>
          ))}

          <p className="text-xs text-muted-foreground">
            You'll get one link to share. Visitors are split between the variants by the weights above,
            and each visitor keeps the variant they first saw.
          </p>
        </div>

        <ModalFooter>
          <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Test
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
